import { useEffect, useState } from "react";

// A minimal hash router. Phase 1 shipped one screen; phase 2 adds a real,
// independently-reachable Node List page and Node Detail page (spec: "a
// real page ... rather than as a popup list"). Not pulling in react-router
// for two routes plus a callback screen — a ~30-line hash parser covers it
// without a new dependency, and hash routing needs no server-side rewrite
// rule on whatever eventually serves the built assets.
//
// Routes:
//   #/                        -> live view (default)
//   #/nodes                   -> node list
//   #/nodes?f=unpositioned    -> node list, preset to the "no position" filter
//   #/nodes/<id>              -> node detail (full page)
//   #/settings                -> settings page (admin only)
//   #/settings?category=<id>  -> preset directly to a category (any CONFIG_CATEGORIES id, e.g. "connections" — the no-connection-configured hint on Live Map/Node List/Packets links here)
//   #/mqtt                    -> MQTT stub page
//   #/analysis                -> detector findings page (nav label "Analysis", renamed 2026-09-30 from "Audit" — route id
//                                stays "audit" internally, see lib/definitions.ts's NAV_DESTINATIONS comment): a top-level
//                                MeshCore|Meshtastic switch, each with its own tabs (lib/definitions.ts's AUDIT_TABS_BY_PROTOCOL)
//   #/analysis?detector=<id>  -> preset to that detector's own protocol + tab (Ask citation deep-link — MUST keep working; lib/definitions.ts's AUDIT_DETECTOR_TAB/AUDIT_DETECTOR_PROTOCOL)
//   #/analysis?submenu=<id>   -> preset directly to a tab (behaviour|reachability|config|regions)
//   #/analysis?protocol=<id>  -> preset directly to a protocol side (meshcore|meshtastic; default meshcore)
//   #/audit, #/audit?...      -> old pre-rename bookmark; parses identically to #/analysis (same params), same
//                                "never 404 an old bookmark" rule as #/coverage below
//   #/coverage                -> retired 2026-09-20 (folded into Audit > Reachability); redirects there
//                                rather than 404ing an old bookmark, same as #/analysis?detector=<id> is preserved.
//   #/messages                -> simulated-only message list, composer, and simulated node management
//   #/packets                 -> byte-level packet inspector ("packet lab")
//   #/packets/decode          -> the Decode tool: paste a MeshCore packet as hex (a view on the Packets page, not a nav destination)
//   #/packets/traces          -> the Traces list: TRACE packets grouped by trace tag (a view on the Packets page)
//   #/packets/traces?tag=<8 hex>[&window=24h|7d|30d] -> the Traces list with that tag open
//   #/health                  -> transport/observer/broker/feature health rollup
//   #/hazards                 -> Alerts (nav label; route id stays "hazards" — see lib/definitions.ts's NAV_DESTINATIONS
//                                comment), three tabs: broadcast, regions, sources
//   #/hazards?tab=<id>        -> preset directly to a tab (broadcast|regions|sources)
//   #/hazards/<id>            -> single hazard event, including its full gating trace
//   #/login                   -> explicit login screen (also shown automatically when unauthenticated)
//   #/auth-callback?...       -> OIDC redirect target

export type Route =
  | { name: "live" }
  | { name: "nodes"; presetFilter: string | null }
  | { name: "node"; id: string }
  | { name: "settings"; presetCategory: string | null; presetGroup: string | null }
  | { name: "mqtt" }
  | { name: "audit"; presetDetector: string | null; presetSubmenu: string | null; presetProtocol: string | null }
  | { name: "messages" }
  | { name: "packets"; view: "feed" | "decode" | "traces"; presetTag: string | null; presetWindow: "24h" | "7d" | "30d" | null }
  | { name: "health" }
  | { name: "hazards"; presetTab: string | null }
  | { name: "hazard-event"; id: string }
  | { name: "login" }
  | { name: "auth-callback"; params: URLSearchParams };

export function navigate(hash: string): void {
  window.location.hash = hash;
}

export function parseRoute(hash: string): Route {
  // hash looks like "#/nodes/!abc123?x=1" or "" (no hash at all yet).
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const body = raw.startsWith("/") ? raw.slice(1) : raw;
  const [pathPart, queryPart] = body.split("?");
  const params = new URLSearchParams(queryPart ?? "");
  const segments = pathPart.split("/").filter(Boolean);

  if (segments[0] === "nodes" && segments[1]) {
    return { name: "node", id: decodeURIComponent(segments[1]) };
  }
  if (segments[0] === "nodes") {
    return { name: "nodes", presetFilter: params.get("f") };
  }
  if (segments[0] === "login") {
    return { name: "login" };
  }
  if (segments[0] === "settings") {
    return { name: "settings", presetCategory: params.get("category"), presetGroup: params.get("group") };
  }
  if (segments[0] === "mqtt") {
    return { name: "mqtt" };
  }
  // "analysis" is the current (2026-09-30) canonical path; "audit" is the
  // pre-rename path, kept working forever so an old bookmark or link never
  // 404s (same "never break a bookmark" rule #/coverage below follows).
  // The internal route name stays "audit" — see lib/definitions.ts's
  // NAV_DESTINATIONS comment for why the route id/backend feature id are
  // deliberately left alone by this display-only rename.
  if (segments[0] === "analysis" || segments[0] === "audit") {
    return {
      name: "audit",
      presetDetector: params.get("detector"),
      presetSubmenu: params.get("submenu"),
      presetProtocol: params.get("protocol"),
    };
  }
  if (segments[0] === "coverage") {
    return { name: "audit", presetDetector: null, presetSubmenu: "reachability", presetProtocol: null };
  }
  if (segments[0] === "messages") {
    return { name: "messages" };
  }
  if (segments[0] === "packets") {
    const view = segments[1] === "decode" ? "decode" : segments[1] === "traces" ? "traces" : "feed";
    const tag = params.get("tag");
    const win = params.get("window");
    return {
      name: "packets",
      view,
      presetTag: view === "traces" && tag !== null && /^[0-9a-f]{8}$/i.test(tag) ? tag.toLowerCase() : null,
      presetWindow: view === "traces" && (win === "24h" || win === "7d" || win === "30d") ? win : null,
    };
  }
  if (segments[0] === "health") {
    return { name: "health" };
  }
  if (segments[0] === "hazards" && segments[1]) {
    return { name: "hazard-event", id: decodeURIComponent(segments[1]) };
  }
  if (segments[0] === "hazards") {
    return { name: "hazards", presetTab: params.get("tab") };
  }
  if (segments[0] === "auth-callback") {
    return { name: "auth-callback", params };
  }
  return { name: "live" };
}

export function useRoute(): Route {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return parseRoute(hash);
}
